/**
 * Whether to draw the optional illustrations, and whether they may move.
 *
 * Three things decide it, and each can only narrow the one before it:
 *
 * 1. The organization permits decorative artwork, or does not.
 * 2. The person chooses subtle motion, a still image, or none.
 * 3. The operating system asks for reduced motion, which nothing overrides.
 *
 * The answer is applied before the first paint. The server's value arrives
 * with the session, and until it does the last known value is read from local
 * storage — otherwise a person who turned artwork off would see it flash on
 * every load. Local storage is a convenience here and never the authority:
 * a private window simply falls back to the default.
 *
 * See `docs/SPACE_DETAILS.md`.
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { api } from "../../lib/api.ts";
import { Icon } from "../icon.tsx";

export type IllustrationChoice = "subtle" | "static" | "off";

export interface PresentationView {
  readonly illustrations: IllustrationChoice;
  readonly organizationAllowsArtwork: boolean;
  readonly effective: IllustrationChoice;
  readonly canManageOrganization: boolean;
}

const STORAGE_KEY = "arcgrc.illustrations";

/** The last known answer, so the first paint is already right. */
function remembered(): IllustrationChoice {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "subtle" || stored === "static" || stored === "off") {
      return stored;
    }
  } catch {
    // A private window forgets it. The default is correct.
  }
  return "subtle";
}

function remember(value: IllustrationChoice): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Nothing depends on this succeeding.
  }
}

/** True when the operating system asks for less movement. */
function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export interface Illustrations {
  /** Draw the artwork at all. */
  readonly show: boolean;
  /** Draw it and let it move once, briefly. */
  readonly motion: boolean;
  /** The person's own choice, for the settings control. */
  readonly choice: IllustrationChoice;
  readonly view: PresentationView | undefined;
}

export function useIllustrations(): Illustrations {
  const query = useQuery({
    queryKey: ["presentation"],
    queryFn: () => api.get<PresentationView>("/api/presentation"),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Until the server answers, use the last known value rather than flashing
  // artwork at somebody who turned it off.
  const effective: IllustrationChoice = query.data?.effective ?? remembered();
  const [reduced, setReduced] = useState(prefersReducedMotion);

  useEffect(() => {
    if (query.data !== undefined) {
      remember(query.data.effective);
    }
  }, [query.data]);

  useEffect(() => {
    let media: MediaQueryList;
    try {
      media = window.matchMedia("(prefers-reduced-motion: reduce)");
    } catch {
      return;
    }
    const onChange = () => setReduced(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  return {
    show: effective !== "off",
    // Reduced motion always wins.
    motion: effective === "subtle" && !reduced,
    choice: query.data?.illustrations ?? effective,
    view: query.data,
  };
}

/**
 * A button that plays one short decorative movement.
 *
 * It runs once per deliberate activation and stops within two seconds. It
 * makes no request, changes no record, and records nothing. With motion off it
 * still reveals the still image, so the button keeps its meaning; with artwork
 * off the caller does not render it at all.
 *
 * The touch target is 44 by 44 CSS pixels even though the icon is smaller.
 */
export function PlayOnceButton({
  label,
  caption,
  children,
  motion,
}: {
  readonly label: string;
  readonly caption: string;
  readonly children: (playing: boolean) => React.ReactNode;
  readonly motion: boolean;
}) {
  const [playing, setPlaying] = useState(false);
  const [shown, setShown] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        // The target is larger than the mark inside it.
        className="inline-flex h-11 w-11 items-center justify-center rounded-arc text-muted hover:bg-selected"
        // Playing it again is the point, so this is not a failed control.
        data-repeat-expected=""
        onClick={() => {
          setShown(true);
          if (!motion) {
            return;
          }
          window.clearTimeout(timer.current);
          setPlaying(true);
          // Every decorative sequence ends within two seconds and then stays
          // still. It runs again only on another deliberate activation.
          timer.current = window.setTimeout(() => setPlaying(false), 1800);
        }}
      >
        <span className="sr-only">{label}</span>
        <Icon name="planet" size="1.25rem" />
      </button>
      <div className="flex items-center gap-2">
        {children(playing)}
        {shown && <span className="text-xs text-muted">{caption}</span>}
      </div>
    </div>
  );
}
